import { useState, useRef, useEffect, useMemo, useId } from 'react';
import {
  Bell,
  X,
  CheckCircle2,
  AlertTriangle,
  Shield,
  Users,
  ExternalLink,
  Loader2,
  Clock,
  TrendingUp,
  Megaphone,
  Info,
  UserPlus,
} from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useClearAllNotifications,
  useDeleteNotification,
  useCurrentUser,
  useUsageCurrent,
} from '../../hooks/useAPI';
import type { Notification, NotificationType } from '../../types/api';
import { ConfirmDialog } from './ConfirmDialog';
import { resolveLocale } from '../../utils/resolveLocale';

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

// System notifications synthesised on the client (trial / quota) get a stable
// timestamp so React-Query rerenders don't churn the "just now" label every
// frame and so the React keys stay reference-stable across renders.
const SYSTEM_TRIAL_ID = 'system-trial';
const SYSTEM_USAGE_ID = 'system-usage';

// Cap how many notifications the dropdown shows. Anything above this
// requires the user to follow the "View all" footer link to the full
// notifications page. Hoisted so the count appears once in the
// "View all N notifications" copy AND in the slice — no drift risk.
const MAX_DROPDOWN_ITEMS = 10;

// URL safety gate for backend-provided actionUrl values. Only internal
// paths (`/...`) and absolute https URLs are allowed. Rejects `javascript:`,
// `data:`, `mailto:`, `vbscript:`, and anything else that could execute
// in-document or leak data via Referer when clicked. Defense-in-depth
// even though backend should already sanitize.
function isSafeActionUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (url.startsWith('/')) return true;
  if (/^https:\/\//i.test(url)) return true;
  return false;
}
function isInternalActionUrl(url: string): boolean {
  return url.startsWith('/');
}

// Map every NotificationType to a deterministic icon. Without this the
// switch fell through to a generic Users icon for system/invite/broadcast/
// alert/info — five of eight enum members were silently miscategorised.
function getIcon(type: NotificationType) {
  switch (type) {
    case 'success':
      return <CheckCircle2 className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'billing':
      return <AlertTriangle className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'security':
      return <Shield className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'alert':
      return <AlertTriangle className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'invite':
      return <UserPlus className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'broadcast':
      return <Megaphone className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'info':
      return <Info className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    case 'system':
      return <Bell className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
    default:
      return <Users className="w-5 h-5 text-muted-foreground" aria-hidden="true" />;
  }
}

function getTimeAgo(dateStr: string | number | undefined): string {
  if (!dateStr) return '';
  const ms = new Date(dateStr).getTime();
  if (!Number.isFinite(ms)) return '';
  const seconds = Math.floor((Date.now() - ms) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function NotificationCenter({ isDemoMode = false }: { isDemoMode?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Stable id pair for the Bell trigger ↔ dialog → heading-label
  // relationship. Pre-fix the dialog had no aria-labelledby (SR named
  // it generically "dialog") and the trigger had aria-haspopup="dialog"
  // but no aria-controls (SR could not jump to the panel via screen
  // reader hot-keys).
  const dialogId = useId();
  const dialogLabelId = useId();

  const { data: notificationsData, isLoading } = useNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const clearAll = useClearAllNotifications();
  const deleteNotification = useDeleteNotification();
  const { data: currentUser } = useCurrentUser();
  const { data: usageCurrent } = useUsageCurrent();

  const notifications: Notification[] = useMemo(
    () => (Array.isArray(notificationsData) ? notificationsData : []),
    [notificationsData],
  );

  // Synthesised system notifications (trial / quota). Stable timestamps so
  // re-renders don't reshuffle the "just now" label every frame.
  const systemNotifications = useMemo<Notification[]>(() => {
    const out: Notification[] = [];
    const now = new Date().toISOString();

    const trialEndsAt = currentUser?.trialEndsAt ? new Date(currentUser.trialEndsAt) : null;
    const trialDaysLeft = trialEndsAt && Number.isFinite(trialEndsAt.getTime())
      ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : null;

    if (trialDaysLeft !== null && trialDaysLeft <= 14) {
      const isExpired = trialDaysLeft === 0;
      const isUrgent = trialDaysLeft <= 3;
      out.push({
        id: SYSTEM_TRIAL_ID,
        type: 'billing',
        title: isExpired
          ? 'Trial expired — upgrade required'
          : isUrgent
          ? `Trial expires in ${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'}`
          : `Free trial: ${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'} remaining`,
        body: isExpired
          ? 'Your free trial has ended. Upgrade now to continue monitoring your AI agents.'
          : 'Upgrade now to continue monitoring your AI agents without interruption.',
        createdAt: now,
        isRead: false,
        link: '/dashboard/billing',
      });
    }

    const limit = usageCurrent?.limit;
    const used = usageCurrent?.thisMonth ?? 0;
    const usagePct = limit != null && limit > 0 ? Math.min(100, (used / limit) * 100) : 0;

    if (usagePct >= 80) {
      // Locale-aware formatting via shared resolveLocale — keeps the
      // synthesised-notification number format in lockstep with the
      // rest of the dashboard (cat 22 cross-section locale consistency).
      const lang = resolveLocale();
      out.push({
        id: SYSTEM_USAGE_ID,
        type: 'billing',
        title: `API quota at ${usagePct.toFixed(0)}%`,
        body: `${(usageCurrent?.remaining ?? 0).toLocaleString(lang)} traces remaining this period.`,
        createdAt: now,
        isRead: false,
        // Direct link to the Billing usage section — /dashboard/usage is a
        // legacy redirect that bounces clients through an extra hop.
        link: '/dashboard/billing#usage',
      });
    }

    return out;
    // currentUser/usageCurrent are React-Query results — referentially stable
    // across renders unless data actually changes, so the memo doesn't churn.
  }, [currentUser?.trialEndsAt, usageCurrent?.limit, usageCurrent?.thisMonth, usageCurrent?.remaining]);

  const allNotifications = useMemo(
    () => [...systemNotifications, ...notifications],
    [systemNotifications, notifications],
  );
  const unreadApiCount = useMemo(
    () => notifications.filter(n => !n.isRead && !n.read).length,
    [notifications],
  );
  const unreadCount = systemNotifications.length + unreadApiCount;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Match the ESC handler — return focus to the Bell trigger so
        // keyboard users don't lose track. Mouse users won't notice
        // the focus change but it's a no-op cost for the win.
        triggerRef.current?.focus();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        // WAI-ARIA dialog pattern: focus must return to the invoking
        // element on dismissal. Pre-fix ESC dropped focus to <body>,
        // forcing keyboard users to Tab back through the entire header.
        triggerRef.current?.focus();
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleMarkAsRead = (id: string) => {
    if (id.startsWith('system-')) return;
    markAsRead.mutate(id, {
      onError: (err) => {
        if (import.meta.env.DEV) {
          console.warn('[NotificationCenter] markAsRead failed:', redactErrorForDev(err));
        }
        toast.error('Failed to mark notification as read.');
      },
    });
  };

  // Single PATCH /read-all replaces the previous N+1 loop (one mutation per
  // unread). 30 unread notifications used to trigger 30 PATCH requests and,
  // on partial failure, stack 30 toast errors.
  const handleMarkAllAsRead = () => {
    if (unreadApiCount === 0) return;
    markAllAsRead.mutate(undefined, {
      onError: (err) => {
        if (import.meta.env.DEV) {
          console.warn('[NotificationCenter] markAllAsRead failed:', redactErrorForDev(err));
        }
        toast.error('Failed to mark notifications as read.');
      },
    });
  };

  const handleClearAll = () => {
    clearAll.mutate(undefined, {
      onSuccess: () => {
        toast.success('Notifications cleared');
        setIsOpen(false);
      },
      onError: (err) => {
        if (import.meta.env.DEV) {
          console.warn('[NotificationCenter] clearAll failed:', redactErrorForDev(err));
        }
        toast.error('Failed to clear notifications. Please try again.');
      },
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-surface-2 border border-transparent hover:border-border transition-colors motion-reduce:transition-none group focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls={dialogId}
      >
        <Bell
          className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors motion-reduce:transition-none"
          aria-hidden="true"
        />
        {unreadCount > 0 && (
          <>
            {/* Mobile: Maritime Blue dot — brand-coloured unread signal,
                consistent with primary CTA + sidebar active stripe.
                Brand acts as the "where Adjudon attention lives" marker. */}
            <span
              className="lg:hidden absolute top-1.5 right-1.5 w-2 h-2 bg-[var(--brand)]"
              aria-hidden="true"
            />
            {/* Desktop: number badge — Maritime Blue with theme-correct
                primary-foreground text (white in light, dark in dark) so
                contrast always passes AAA without per-theme overrides. */}
            <span
              className="hidden lg:flex absolute top-1 right-1 min-w-[14px] h-[14px] bg-[var(--brand)] text-[var(--primary-foreground)] text-[8px] font-bold items-center justify-center px-0.5 tabular-nums"
              aria-hidden="true"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/60 animate-in fade-in-0 duration-150 motion-reduce:animate-none"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Notification Panel
              role="dialog" without aria-modal — this is a transient dropdown,
              not a modal. aria-modal=true would claim a focus trap that the
              header dropdown intentionally does not implement (Tab moves on
              to the next header item, mirroring Stripe / Linear menus).
              aria-labelledby points at the visible h3 heading so SR users
              hear the section name on focus instead of generic "dialog". */}
          <div
            id={dialogId}
            role="dialog"
            aria-labelledby={dialogLabelId}
            // w-96 = 384px overflows on small phones (iPhone SE = 320px).
            // min(384px, calc(100vw - 16px)) keeps an 8px gutter on each
            // side without depending on a TW arbitrary value chain.
            className="absolute top-full right-0 mt-2 adp-dropdown z-50 animate-in slide-in-from-top-2 fade-in-0 duration-150 motion-reduce:animate-none"
            style={{ width: 'min(384px, calc(100vw - 16px))' }}
          >
            {/* Header */}
            <div className="border-b border-border p-4 bg-surface-2">
              <div className="flex items-center justify-between mb-2">
                <h3 id={dialogLabelId} className="text-sm text-mono uppercase tracking-wider font-semibold text-foreground">
                  Notifications
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    // Focus restoration on explicit Close — same pattern
                    // as ESC + click-outside. Without it, keyboard users
                    // who Tab to the X to dismiss are stranded at <body>.
                    triggerRef.current?.focus();
                  }}
                  className="p-1 hover:bg-surface-1 transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                  aria-label="Close notifications"
                >
                  <X className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                </button>
              </div>
              {unreadCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {unreadCount} unread
                  </span>
                  {unreadApiCount > 0 && !isDemoMode && (
                    <button
                      type="button"
                      onClick={handleMarkAllAsRead}
                      disabled={markAllAsRead.isPending}
                      aria-busy={markAllAsRead.isPending}
                      className="text-xs text-foreground underline-offset-2 hover:underline disabled:opacity-50 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                    >
                      {markAllAsRead.isPending ? 'Marking…' : 'Mark all as read'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Notification List — cap with min(480px, 80dvh) so the
                panel doesn't overflow the mobile viewport. dvh accounts
                for browser-chrome collapse on iOS Safari which vh
                overshoots. */}
            <div className="overflow-y-auto" style={{ maxHeight: 'min(480px, 80dvh)' }}>
              {isLoading ? (
                <div className="p-8 text-center" aria-busy="true">
                  <Loader2
                    className="w-6 h-6 text-muted-foreground/40 mx-auto mb-3 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  <p className="text-xs text-muted-foreground">Loading…</p>
                </div>
              ) : allNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell
                    className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-muted-foreground">No notifications</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You're all caught up!
                  </p>
                </div>
              ) : (
                // Semantic <ul role="list"> wrapper so SR users get the
                // count + can navigate by list (e.g., NVDA "list with N
                // items" announcement). Pre-fix items were sibling <div>s
                // — invisible to list-navigation rotor.
                <ul role="list" className="m-0 p-0 list-none">
                {allNotifications.slice(0, MAX_DROPDOWN_ITEMS).map((notification) => {
                  // Type guard against backend drift — `id` is typed required
                  // but a pre-R2 lean-doc Mongo response (no `id` virtual)
                  // would otherwise throw TypeError on `.startsWith` and
                  // crash the entire notification list (cat 27 defensive).
                  const isSystemNotification = typeof notification.id === 'string'
                    && notification.id.startsWith('system-');
                  const isRead = notification.isRead ?? notification.read ?? false;
                  // Notification type already aliases legacy field names
                  // (body|message, createdAt|timestamp, link|actionUrl), so
                  // we read both branches without `as any` casts.
                  const body = notification.body ?? notification.message ?? '';
                  const actionUrl = notification.link ?? notification.actionUrl;
                  const timestamp = notification.createdAt ?? notification.timestamp;
                  const icon = notification.id === SYSTEM_TRIAL_ID
                    ? <Clock className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
                    : notification.id === SYSTEM_USAGE_ID
                    ? <TrendingUp className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
                    : getIcon(notification.type);
                  return (
                    <li
                      key={notification.id}
                      className={`group border-b border-border p-4 hover:bg-surface-2 transition-colors motion-reduce:transition-none ${
                        !isRead ? 'bg-surface-1' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">{icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="text-sm font-semibold text-foreground">
                              {notification.title}
                            </h4>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {!isRead && (
                                // sr-only span instead of bare <div aria-label> —
                                // some SR stacks silently drop aria-label on
                                // generic divs. role="img" + sr-only text gives
                                // both AT pickup AND visual indicator.
                                <span className="relative inline-flex" role="img" aria-label="Unread">
                                  <span aria-hidden="true" className="w-2 h-2 bg-foreground mt-1.5 block" />
                                </span>
                              )}
                              {!isDemoMode && !isSystemNotification && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification.mutate(notification.id, {
                                      onError: (err) => {
                                        if (import.meta.env.DEV) {
                                          console.warn('[NotificationCenter] deleteNotification failed:', redactErrorForDev(err));
                                        }
                                        toast.error('Failed to delete notification.');
                                      },
                                    });
                                  }}
                                  // Title in label so SR users with multiple
                                  // delete buttons in their navigation list
                                  // can disambiguate ("Delete notification:
                                  // Trial expires in 3 days").
                                  // focus-visible:opacity-100 so keyboard
                                  // users can find the delete button when
                                  // they Tab into it (group-hover hides it
                                  // visually until mouse-hover).
                                  aria-label={`Delete notification: ${notification.title}`}
                                  className="p-0.5 hover:bg-surface-1 transition-colors motion-reduce:transition-none opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                                >
                                  <X className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
                            {body}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-mono text-muted-foreground">
                              {getTimeAgo(timestamp)}
                            </span>
                            {isSafeActionUrl(actionUrl) && actionUrl && (
                              isDemoMode ? (
                                <button
                                  type="button"
                                  disabled
                                  className="text-xs text-muted-foreground/40 cursor-not-allowed flex items-center gap-1"
                                >
                                  <span>View</span>
                                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                                </button>
                              ) : isInternalActionUrl(actionUrl) ? (
                                // Internal link → react-router. Pre-fix
                                // EVERY actionUrl went through <Link to={...}>
                                // — if backend ever emitted an external
                                // URL (https://...) Link silently treated
                                // it as a relative path and stuck the user
                                // on /dashboard/https://... a 404. Plus
                                // isSafeActionUrl rejects javascript: /
                                // data: / mailto: / vbscript: schemes
                                // entirely (defense-in-depth XSS).
                                <Link
                                  to={actionUrl}
                                  onClick={() => {
                                    handleMarkAsRead(notification.id);
                                    setIsOpen(false);
                                  }}
                                  className="text-xs text-foreground underline-offset-2 hover:underline flex items-center gap-1 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                                >
                                  <span>View</span>
                                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                                </Link>
                              ) : (
                                // External link → real <a>, new tab,
                                // noopener+noreferrer (security: prevents
                                // window.opener leak + Referer header
                                // exposing dashboard origin to 3rd party).
                                <a
                                  href={actionUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => {
                                    handleMarkAsRead(notification.id);
                                    setIsOpen(false);
                                  }}
                                  className="text-xs text-foreground underline-offset-2 hover:underline flex items-center gap-1 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                                >
                                  <span>View</span>
                                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                                </a>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
                </ul>
              )}
            </div>

            {/* Footer */}
            {allNotifications.length > 0 && (
              <div className="border-t border-border p-3 bg-surface-2 flex items-center justify-between">
                {isDemoMode ? (
                  <button
                    type="button"
                    disabled
                    className="text-xs text-muted-foreground/40 cursor-not-allowed flex items-center gap-1"
                  >
                    <span>View all notifications</span>
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </button>
                ) : (
                  <Link
                    to="/dashboard/notifications"
                    onClick={() => setIsOpen(false)}
                    className="text-xs text-foreground underline-offset-2 hover:underline flex items-center gap-1"
                  >
                    <span>
                      {allNotifications.length > MAX_DROPDOWN_ITEMS
                        ? `View all ${allNotifications.length} notifications`
                        : 'View all notifications'}
                    </span>
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </Link>
                )}
                <button
                  type="button"
                  // Pre-fix: clicking "Clear all" instantly DELETED every
                  // notification with no confirmation. Same destructive-
                  // action class as SCIM Disable / Delete Org. Now gated
                  // by ConfirmDialog so a misclick can't wipe history.
                  onClick={() => setConfirmClearOpen(true)}
                  disabled={clearAll.isPending || isDemoMode || allNotifications.length === 0}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors motion-reduce:transition-none disabled:opacity-50"
                >
                  {clearAll.isPending ? 'Clearing…' : 'Clear all'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirm={() => {
          handleClearAll();
          setConfirmClearOpen(false);
        }}
        title="Clear all notifications?"
        description="This permanently deletes every notification from your inbox — including unread alerts. System notifications (trial / quota) will reappear if their conditions still apply."
        confirmText="Clear all"
        cancelText="Cancel"
        variant="danger"
        icon={<AlertTriangle className="w-5 h-5 text-muted-foreground" aria-hidden="true" />}
      />
    </div>
  );
}
